import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  EmptyStation,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import * as EmailValidator from 'email-validator';
import { Field } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  CreateEmailWhitelistPatternDocument,
  type CreateEmailWhitelistPatternMutation,
  type CreateEmailWhitelistPatternMutationVariables,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';

type FormData =
  CreateEmailWhitelistPatternMutationVariables['input']['emailWhitelistPattern'];

const wildcardReplacement = 'ax';
export const emailWhitelistPatternSchema = Yup.object<
  ObjectSchemaDefinition<FormData>
>({
  emailPattern: Yup.string()
    .label('Email Whitelist Pattern')
    .required()
    .max(200)
    .test(
      'email-whitelist-pattern',
      'Email Whitelist Pattern is not valid.',
      function (pattern: string | undefined) {
        if (pattern !== undefined) {
          if (pattern === '*') {
            return true;
          }

          // Prevent adjacent repetition of wildcard character '*'
          if (pattern.indexOf('**') !== -1) {
            return false;
          }

          // Replace wildcard character with a fixed replacement string, and validate the resulting email
          const email = pattern.replaceAll('*', wildcardReplacement);
          return EmailValidator.validate(email);
        }
        return false;
      },
    ),
  environmentId: Yup.string(),
});

type SubmitResponse = Nullable<
  CreateEmailWhitelistPatternMutation['createEmailWhitelistPattern']
>;

export const EmailWhitelistPatternCreate: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const initialFormValues: FormData = {
    emailPattern: '',
    environmentId: envId,
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.ID_SERVICE,
          envId,
        );
        setServiceStatusMessage(statusMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      // Save email whitelist pattern
      const response = (
        await apolloClient.mutate<
          CreateEmailWhitelistPatternMutation,
          CreateEmailWhitelistPatternMutationVariables
        >({
          mutation: CreateEmailWhitelistPatternDocument,
          variables: {
            input: {
              emailWhitelistPattern: {
                emailPattern: formData.emailPattern,
                environmentId: envId,
              },
            },
          },
          fetchPolicy: 'no-cache',
        })
      ).data?.createEmailWhitelistPattern;

      return response ?? null;
    },
    [envId, apolloClient],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const emailWhitelistPattern = submitResponse?.emailWhitelistPattern;
      if (emailWhitelistPattern) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns/${emailWhitelistPattern.id}`,
        );
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [envId, history],
  );

  const [message] = useState<StationMessage>({
    title:
      "Email whitelist pattern supports the '*' (asterisk) wildcard character (valid examples: name@domain.com, *@domain.com, department@*.domain.com).",
    type: 'info',
  });

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="New Email Whitelist Pattern">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Create<FormData, SubmitResponse>
          title="New Email Whitelist Pattern"
          validationSchema={emailWhitelistPatternSchema}
          saveData={saveData}
          onProceed={onProceed}
          cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.ID_SERVICE}/config/emailWhitelistPatterns`}
          initialData={{
            data: initialFormValues,
            loading,
          }}
          stationMessage={message}
        >
          <Field
            name="emailPattern"
            label="Email Whitelist Pattern"
            as={SingleLineTextField}
          />
        </Create>
      )}
    </>
  );
};

export const EmailWhitelistPatternCreateCrumb: BreadcrumbResolver = () =>
  'New Email Pattern';
